import { match } from 'ts-pattern'
import { FileFormatIcons } from '~/components/FileFormatIcons'
import { LogoIcons } from '~/components/LogoIcons'
import { Badge, Icon } from '~/elements'
import { KnowledgeSourceType } from '~/features/knowledge-bases/types'
import { Color } from '~/types'

type AddKnowledgeSourceButtonProps = {
  type: KnowledgeSourceType
  onClick: () => void
}

const { pdfIcon, typeIcon, urlIcon, txtIcon } = FileFormatIcons
const { googleDriveIcon, notionIcon, confluenceIcon } = LogoIcons

export const AddKnowledgeSourceButton = ({ type, onClick }: AddKnowledgeSourceButtonProps) => {
  type MatchOutput = {
    color: Color
    badge: string
    description: string
    icon: React.FC<React.SVGProps<SVGSVGElement>>
  }

  const { color, badge, description, icon } = match<KnowledgeSourceType, MatchOutput>(type)
    .with('document', () => ({
      color: 'tomato',
      badge: 'Document',
      description: 'Upload documents',
      icon: pdfIcon,
    }))
    .with('rich-text', () => ({
      color: 'pink',
      badge: 'Inline Document',
      description: 'Create a snippet',
      icon: typeIcon,
    }))
    .with('web-page', () => ({
      color: 'indigo',
      badge: 'Website',
      description: 'Sync content from a website',
      icon: urlIcon,
    }))
    .with('google-drive', () => ({
      color: 'green',
      badge: 'Google Drive',
      description: 'Sync content from Google Drive',
      icon: googleDriveIcon,
    }))
    .with('notion', () => ({
      color: 'gray',
      badge: 'Notion',
      description: 'Sync content from Notion',
      icon: notionIcon,
    }))
    .with('confluence', () => ({
      color: 'blue',
      badge: 'Confluence',
      description: 'Sync content from Confluence',
      icon: confluenceIcon,
    }))
    .exhaustive()

  return (
    <div
      onClick={onClick}
      className="flex cursor-pointer overflow-hidden rounded-lg border border-border transition-colors hover:bg-muted"
    >
      <div className="flex items-center justify-center bg-muted px-7 py-8">
        <Icon icon={icon} className="h-10 w-10" />
      </div>
      <div className="flex flex-col gap-2 px-4 py-6">
        <Badge className="w-fit" color={color} variant="outline">
          {badge}
        </Badge>
        <p className="whitespace-nowrap text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  )
}
